import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight, MapPin, X } from 'lucide-react';
import type { Project } from '../data/site';
type ProjectLightboxProps = {
  project: Project | null;
  onClose: () => void;
};
export function ProjectLightbox({
  project,
  onClose
}: ProjectLightboxProps) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
  }, [project?.id]);
  useEffect(() => {
    if (!project) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIndex(i => (i + 1) % project.gallery.length);
      if (e.key === 'ArrowLeft') setIndex(i => (i - 1 + project.gallery.length) % project.gallery.length);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [project, onClose]);
  return <AnimatePresence data-id="element-316" data-mp="3m4aohloiooxn">
      {project && <motion.div key="lightbox" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[70] flex items-center justify-center bg-navy-900/95 p-4 sm:p-8" role="dialog" aria-modal="true" aria-label={`${project.name} project details`} onClick={onClose} data-id="element-317" data-mp="3r2lzw0ugn7hz">
          <motion.div initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} onClick={e => e.stopPropagation()} className="relative grid max-h-[90vh] w-full max-w-6xl overflow-y-auto bg-white lg:grid-cols-[1.35fr_1fr]" data-id="element-318" data-mp="37fnzstd02ktr">
            <button type="button" onClick={onClose} aria-label="Close project" className="absolute right-0 top-0 z-10 inline-flex h-12 w-12 items-center justify-center bg-navy text-white transition hover:bg-brand-500" data-id="element-319" data-mp="wkqsabvol091">
              <X className="h-5 w-5" data-id="element-320" data-mp="l7qfeuj4t230" />
            </button>

            <div className="relative aspect-[4/3] w-full bg-navy-900 lg:aspect-auto lg:min-h-[520px]" data-id="element-321" data-mp="g96riokz9g80">
              <AnimatePresence mode="wait" data-id="element-322" data-mp="1dv48uinmffid">
                <motion.img key={project.gallery[index]} src={project.gallery[index]} alt={`${project.name} — image ${index + 1}`} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="absolute inset-0 h-full w-full object-cover" data-id="element-323" data-mp="1dy05m3nqfy58" />
              </AnimatePresence>

              {project.gallery.length > 1 && <div className="absolute bottom-4 left-4 flex items-center gap-2" data-id="element-324" data-mp="2052tfe9l8tba">
                  <button type="button" aria-label="Previous image" onClick={() => setIndex(i => (i - 1 + project.gallery.length) % project.gallery.length)} className="inline-flex h-10 w-10 items-center justify-center bg-white/90 text-navy transition hover:bg-brand-500 hover:text-white" data-id="element-325" data-mp="24666xu45yd9c">
                    <ChevronLeft className="h-5 w-5" data-id="element-326" data-mp="2n50ple6qzxus" />
                  </button>
                  <button type="button" aria-label="Next image" onClick={() => setIndex(i => (i + 1) % project.gallery.length)} className="inline-flex h-10 w-10 items-center justify-center bg-white/90 text-navy transition hover:bg-brand-500 hover:text-white" data-id="element-327" data-mp="2n2xq83jpgsde">
                    <ChevronRight className="h-5 w-5" data-id="element-328" data-mp="36u2quvuq35fl" />
                  </button>
                  <span className="ml-2 font-mono text-xs text-white/70" data-id="element-329" data-mp="7eh5pkuk4ydm">
                    {index + 1} / {project.gallery.length}
                  </span>
                </div>}
            </div>

            <div className="flex flex-col p-8 sm:p-10" data-id="element-330" data-mp="h3s86omxv4sj">
              <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-600" data-id="element-331" data-mp="c5qqo855vdj6">
                {project.type}
              </span>
              <h3 className="mt-4 font-heading text-3xl font-extrabold uppercase leading-tight tracking-tight text-navy" data-id="element-332" data-mp="3kmbtt8b43ced">
                {project.name}
              </h3>
              <p className="mt-3 flex items-center gap-2 text-sm text-mist-600" data-id="element-333" data-mp="3tmqi8gjdh7m3">
                <MapPin className="h-4 w-4 text-brand-500" aria-hidden="true" data-id="element-334" data-mp="3pkvez6hvsk5o" />
                {project.location} · {project.year}
              </p>

              <p className="mt-6 text-base leading-relaxed text-mist-600" data-id="element-335" data-mp="18uetvyc7ww8j">{project.summary}</p>

              <h4 className="mt-9 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy" data-id="element-336" data-mp="14tmndcuqd5fy">Scope of work</h4>
              <ul className="mt-4 space-y-3 border-t border-mist-200 pt-4" data-id="element-337" data-mp="1ccm3n9mwagv3">
                {project.scope.map(s => <li key={s} className="flex items-center gap-3 text-sm text-mist-600" data-id="element-338" data-mp="12fr5irujaihc">
                    <span className="h-px w-4 bg-brand-500" aria-hidden="true" data-id="element-339" data-mp="12i3wtvm6m572" />
                    {s}
                  </li>)}
              </ul>

              <a href="#contact" onClick={onClose} className="group mt-10 inline-flex items-center justify-center gap-3 bg-brand-500 px-7 py-4 text-[12px] font-bold uppercase tracking-[0.16em] text-white transition hover:bg-brand-600 lg:mt-auto lg:pt-4" data-id="element-340" data-mp="1v5pyuzfrh1rt">
                Start a project like this
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true" data-id="element-341" data-mp="2e3j07xaql3n3" />
              </a>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}